// ============================================================
// MyGene Design System — Design Tokens
// Compact, information-dense medical UI for practitioners.
// Modelled after Medical Director / Best Practice but modernised.
// Dark-first; light theme provided via .light-theme on <html>.
//
// IMPORTANT: These are the TypeScript constants used by
// themeUtils.ts to configure Ant Design's ConfigProvider.
// The CSS custom property equivalents (--mg-*) are injected
// at runtime by themeUtils and declared in global.css.
// ============================================================

// ── SPACING ─────────────────────────────────────────────────
// 4-px base compact scale. Use for all margin/padding/gap.
export const SPACING = {
	xxs: 2,
	xs:  4,
	sm:  8,
	md:  16,
	lg:  24,
	xl:  32,
	xxl: 48,
	'3xl': 64,
} as const;

// ── LAYOUT ──────────────────────────────────────────────────
// Every constant here maps to a --mg-* CSS variable injected by themeUtils.
export const LAYOUT = {
	sidebarWidth: 232,            // px — was 256; gain 24px of canvas
	sidebarWidthCollapsed: 64,    // px — collapsed icon rail
	navItemHeight: 34,            // px — sidebar nav item
	topbarHeight: 44,             // px — was 48; tighter chrome
	contentPadding: 24,           // px — outer padding inside pages
	cardGap: 16,                  // px — gap between sibling cards
	cardBodyPadding: 16,          // px — card body (was 20; now 16)
	cardHeadHeight: 42,           // px — card header row (was 48)
	sectionGap: 24,               // px — gap between page sections
	maxContentWidth: 1400,        // px — optional cap for wide monitors
	rowHeight: 36,                // px — standard table row
	rowHeightDense: 30,           // px — lab / audit / results tables
} as const;

// ── BORDER RADIUS ───────────────────────────────────────────
// Tighter than before — clinical tools feel precise, not bubbly.
export const RADIUS = {
	xs:   3,     // tags, badges
	sm:   4,     // buttons, inputs, nav items
	md:   6,     // dropdowns, tooltips, modals
	lg:   8,     // cards, panels
	xl:   12,    // large modals, popovers
	pill: 9999,  // avatars, pill badges
} as const;

// ── TYPOGRAPHY ──────────────────────────────────────────────
// COMPACT scale — base is 12 px (down from 14).
// ~15% more rows visible in the same viewport.
// Never go below 10 px for legibility.
export const TYPOGRAPHY = {
	size: {
		xxs:  10,   // uppercase eyebrows, badge counters, micro meta
		xs:   11,   // table footer, captions, sub-tab counts
		sm:   11,   // secondary text alt / label
		base: 12,   // BASE — body, inputs, nav, table cells
		md:   14,   // section headings
		lg:   16,   // modal titles, large labels
		xl:   20,   // primary page titles
		'2xl': 24,  // large headings
		'3xl': 28,  // KPI values
	},
	weight: {
		normal:   400,
		medium:   500,
		semibold: 600,
		bold:     700,
	},
	leading: {
		tight:   1.25,
		normal:  1.45,
		relaxed: 1.6,
	},
	fontFamily:
		"-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', sans-serif",
	fontFamilyMono:
		"'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, Consolas, monospace",
} as const;

// ── ELEVATION / SHADOWS ─────────────────────────────────────
// Cards at rest = no shadow; 1-px border is the separator.
// Hover lifts to shadow-sm. Modals use shadow-lg.
export const SHADOWS = {
	dark: {
		none:    'none',
		xs:      '0 1px 2px rgba(0,0,0,0.40)',
		sm:      '0 2px 6px rgba(0,0,0,0.45)',
		md:      '0 4px 14px rgba(0,0,0,0.50)',
		lg:      '0 8px 28px rgba(0,0,0,0.55)',
		xl:      '0 16px 48px rgba(0,0,0,0.60)',
		popover: '0 12px 36px rgba(0,0,0,0.60), 0 0 0 1px rgba(255,255,255,0.05)',
		inset:   'inset 0 1px 0 rgba(255,255,255,0.05)',
	},
	light: {
		none:    'none',
		xs:      '0 1px 2px rgba(0,0,0,0.06)',
		sm:      '0 2px 6px rgba(0,0,0,0.08)',
		md:      '0 4px 14px rgba(0,0,0,0.10)',
		lg:      '0 8px 28px rgba(0,0,0,0.12)',
		xl:      '0 16px 48px rgba(0,0,0,0.14)',
		popover: '0 12px 36px rgba(0,0,0,0.16), 0 0 0 1px rgba(0,0,0,0.04)',
		inset:   'inset 0 1px 0 rgba(0,0,0,0.04)',
	},
} as const;

// ── MOTION ──────────────────────────────────────────────────
// Decelerating standard easing. No bouncy springs. No parallax.
export const MOTION = {
	duration: {
		instant: '0ms',
		fast:    '120ms',
		normal:  '200ms',
		slow:    '350ms',
	},
	easing: {
		standard:   'cubic-bezier(0.16, 1, 0.3, 1)',
		decelerate: 'cubic-bezier(0, 0, 0.2, 1)',
		accelerate: 'cubic-bezier(0.4, 0, 1, 1)',
	},
} as const;

// ── COLOUR SURFACES (theme-agnostic token refs) ──────────────
// These map to CSS custom properties set by themeUtils.
export const SURFACE_VARS = {
	bg:           'var(--mg-bg)',
	bgSubtle:     'var(--mg-bg-subtle)',
	bgElevated:   'var(--mg-bg-elevated)',
	bgCard:       'var(--mg-bg-card)',
	border:       'var(--mg-border)',
	borderStrong: 'var(--mg-border-strong)',
	text:         'var(--mg-text)',
	textSecondary:'var(--mg-text-secondary)',
	textDisabled: 'var(--mg-text-disabled)',
	sidebar:      'var(--mg-sidebar-bg)',
	sidebarBorder:'var(--mg-sidebar-border)',
} as const;

// ── ANT DESIGN COMPONENT OVERRIDES ──────────────────────────
// Passed to ConfigProvider components: { ... }
export const ANT_COMPONENT_TOKENS = {
	Button: {
		controlHeight:           32,    // was 34 — compact
		controlHeightSM:         24,    // was 26
		controlHeightLG:         40,    // was 42
		paddingContentHorizontal: 14,   // was 16
		borderRadius:            RADIUS.sm,
		borderRadiusSM:          RADIUS.xs,
		borderRadiusLG:          RADIUS.md,
		fontSize:                12,
	},
	Input: {
		controlHeight:   32,
		controlHeightSM: 24,
		controlHeightLG: 40,
		paddingBlock:    4,
		borderRadius:    RADIUS.sm,
		fontSize:        12,
	},
	Select: {
		controlHeight:   32,
		controlHeightSM: 24,
		controlHeightLG: 40,
		borderRadius:    RADIUS.sm,
		fontSize:        12,
	},
	DatePicker: {
		controlHeight: 32,
		borderRadius:  RADIUS.sm,
		fontSize:      12,
	},
	Table: {
		cellPaddingBlock:  8,    // was 10 — denser rows
		cellPaddingInline: 12,   // was 14
		borderRadius:      RADIUS.lg,
		borderRadiusOuter: RADIUS.lg,
		fontSize:          12,
	},
	Card: {
		paddingLG:       SPACING.md,   // 16 — was 24 (SPACING.lg)
		borderRadius:    RADIUS.lg,
		borderRadiusLG:  RADIUS.lg,
	},
	Modal: {
		borderRadius:      RADIUS.xl,
		borderRadiusOuter: RADIUS.xl,
	},
	Drawer: {
		borderRadius: 0,
	},
	Tag: {
		borderRadius: RADIUS.xs,
		fontSize:     11,
	},
	Badge: {
		borderRadius: RADIUS.pill,
		fontSize:     10,
	},
	Menu: {
		itemBorderRadius:    RADIUS.sm,
		subMenuItemBorderRadius: RADIUS.xs,
		fontSize:            12,
	},
	Tabs: {
		cardGutter:   2,
		inkBarColor:  'var(--mg-primary)',
		fontSize:     12,
	},
	Tooltip: {
		borderRadius: RADIUS.sm,
		fontSize:     11,
	},
	Dropdown: {
		borderRadius: RADIUS.md,
		fontSize:     12,
	},
	Notification: {
		borderRadius: RADIUS.md,
		fontSize:     12,
	},
	Message: {
		borderRadius: RADIUS.md,
		fontSize:     12,
	},
	Form: {
		labelFontSize: 12,
		itemMarginBottom: 14,
	},
	Pagination: {
		fontSize: 12,
	},
} as const;

// ── BREAKPOINTS ─────────────────────────────────────────────
export const BREAKPOINTS = {
	xs:  480,
	sm:  576,
	md:  768,
	lg:  992,
	xl:  1200,
	xxl: 1600,
} as const;

// ── Z-INDEX SCALE ────────────────────────────────────────────
export const Z_INDEX = {
	sidebar:  100,
	topbar:    98,
	overlay:  500,
	modal:   1000,
	popover: 1050,
	toast:   1100,
} as const;
